import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@ndd/ui";
import { cn } from "@/lib/utils";

export interface RdComboboxRd {
  id: string;
  name: string;
}

export function RdCombobox({
  existingRds,
  selectedRdId,
  disabled,
  onSelect,
}: {
  existingRds: RdComboboxRd[];
  selectedRdId: string | null;
  disabled: boolean;
  onSelect: (rdId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedLabel = selectedRdId
    ? (existingRds.find((rd) => rd.id === selectedRdId)?.name ?? "Unknown")
    : "Select RD...";

  const filtered = existingRds.filter((rd) =>
    rd.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "justify-between rounded-lg border-border font-normal",
            !selectedRdId && "text-muted-foreground"
          )}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <RdCommandList
          rds={filtered}
          search={search}
          onSearchChange={setSearch}
          selectedRdId={selectedRdId}
          onSelect={(rdId) => {
            onSelect(rdId);
            setOpen(false);
            setSearch("");
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function RdCommandList({
  rds,
  search,
  onSearchChange,
  selectedRdId,
  onSelect,
}: {
  rds: RdComboboxRd[];
  search: string;
  onSearchChange: (value: string) => void;
  selectedRdId: string | null;
  onSelect: (rdId: string) => void;
}) {
  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder="Search RDs..."
        value={search}
        onValueChange={onSearchChange}
      />
      <CommandList>
        <CommandEmpty>No RD found.</CommandEmpty>
        <CommandGroup>
          {rds.map((rd) => (
            <CommandItem key={rd.id} value={rd.id} onSelect={() => onSelect(rd.id)}>
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  selectedRdId === rd.id ? "opacity-100" : "opacity-0"
                )}
              />
              {rd.name}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
